import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API_URL } from '../../types';
import { useTranslation } from '../../i18n';
import { LatLng } from '../LocationMap';
import { SEARCH_RADIUS_OPTIONS_KM, SearchLocationSource } from '../../utils/searchLocation';
import { ProductFilter } from './ProductFilters';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const pickIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const LOCATION_FILTER_ID = 'location';

const summaryKey: Record<SearchLocationSource, string> = {
  device: 'products.location_summary_device',
  prague: 'products.location_summary_prague',
  custom: 'products.location_summary_custom',
};

const FitSearchView: React.FC<{ center: LatLng; radiusKm: number }> = ({ center, radiusKm }) => {
  const map = useMap();

  useEffect(() => {
    const radiusM = radiusKm * 1000;
    const padLat = radiusM / 111_320;
    const padLng = radiusM / (111_320 * Math.cos((center.lat * Math.PI) / 180));
    const bounds = L.latLngBounds([
      [center.lat + padLat, center.lng + padLng],
      [center.lat - padLat, center.lng - padLng],
    ]);
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 14 });
  }, [map, center, radiusKm]);

  return null;
};

const MapClickPicker: React.FC<{ onPick: (point: LatLng) => void }> = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
};

export interface LocationFilterProps {
  center: LatLng;
  radiusKm: number;
  locationSource: SearchLocationSource;
  onCenterChange: (point: LatLng) => void;
  onRadiusChange: (km: number) => void;
  onUseMyLocation: () => void;
}

export const LocationFilter: React.FC<LocationFilterProps> = ({
  center,
  radiusKm,
  locationSource,
  onCenterChange,
  onRadiusChange,
  onUseMyLocation,
}) => {
  const { t } = useTranslation();
  const tileUrl = `${API_URL}/mapy/tiles/basic/{z}/{x}/{y}`;
  const radiusM = radiusKm * 1000;
  const summary = t(summaryKey[locationSource], { km: radiusKm });

  return (
    <ProductFilter id={LOCATION_FILTER_ID} summary={summary}>
      <div className="location-filter">
        <div className="location-filter__header">
          <span className="location-filter__label">{t('products.location_filter')}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onUseMyLocation}>
            {t('products.location_use_mine')}
          </button>
        </div>
        <div className="location-filter__map">
          <MapContainer
            center={[center.lat, center.lng]}
            zoom={12}
            style={{ height: 220, width: '100%', borderRadius: 'var(--radius, 8px)' }}
            scrollWheelZoom
          >
            <TileLayer
              url={tileUrl}
              attribution='&copy; <a href="https://mapy.com/">Mapy.com</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            <FitSearchView center={center} radiusKm={radiusKm} />
            <MapClickPicker onPick={onCenterChange} />
            <Circle
              center={[center.lat, center.lng]}
              radius={radiusM}
              pathOptions={{ color: '#E8604C', fillColor: '#E8604C', fillOpacity: 0.12, weight: 2 }}
            />
            <Marker position={[center.lat, center.lng]} icon={pickIcon} />
          </MapContainer>
        </div>
        <p className="text-muted location-filter__map-hint">{t('products.location_map_hint')}</p>
        <label className="location-filter__distance">
          <span>{t('products.location_max_distance')}</span>
          <select value={radiusKm} onChange={e => onRadiusChange(Number(e.target.value))}>
            {SEARCH_RADIUS_OPTIONS_KM.map(km => (
              <option key={km} value={km}>
                {t('products.location_km_option', { km })}
              </option>
            ))}
          </select>
        </label>
      </div>
    </ProductFilter>
  );
};
